import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { dateTime, num } from '../lib/format.js';
import {
  BROADCAST_STATUS_LABEL,
  BROADCAST_STATUS_TONE,
  CANARY_STEPS_LABEL,
  REACTION_LABEL,
  REASON_LABEL,
  RECIPIENT_STATUS_LABEL,
  WINDOW_MODE_LABEL,
  label,
} from '../lib/labels.js';
import { useMe } from '../lib/session.js';
import type { Rights } from '../lib/session.js';
import { EnvBadge } from '../components/EnvironmentSwitcher.js';
import {
  ConfirmDialog,
  EmptyState,
  Bar,
  ErrorState,
  Field,
  Icon,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import { StaticLists } from './StaticLists.js';
import { raceOptionLabel } from './BroadcastPlan.js';
import '../screens-broadcasts.css';

/**
 * Rozesílky (vlna 1, docs/ROZESILKY.md): jednorázové marketingové odeslání šablony na seznam.
 * Průvodce je detail rozesílky: kroky se odškrtávají podle stavu a každá akce má vlastní potvrzení.
 */

interface Preview {
  total: number;
  willSend: number;
  reasons: Array<{ status: string; reason: string; count: number }>;
  sample: Array<{ email: string; status: string; reason: string | null; language: string | null }>;
  mode: string;
  globalHold: string | null;
  languages: Array<{ language: string; count: number }>;
}

/** Výpočet náhledu velkého seznamu na pozadí (docs/ROZESILKY.md). */
interface PreviewJob {
  runId: string;
  state: 'running' | 'done' | 'cancelled' | 'failed';
  claimed: boolean;
  total: number | null;
  processed: number;
  startedAt: string;
  updatedAt: string;
  finishedAt: string | null;
  error: string | null;
}

/** Odpověď na „Zkontrolovat příjemce“: výsledek hned, nebo výpočet na pozadí. */
type PreviewResponse = Preview | { computing: true; job: PreviewJob };

interface Metrics {
  planned: number;
  sent: number;
  held: number;
  suppressed: number;
  failed: number;
  expired: number;
  pending: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  badRate: number;
  attempts: number;
}

export interface BroadcastView {
  id: number;
  code: string;
  name: string;
  brand: string;
  templateBase: string;
  fallbackLanguage: string;
  audience: {
    kind: string;
    segmentCode: string | null;
    staticListId: string | null;
    label: string;
  };
  windowMode: string;
  canaryEnabled: boolean;
  status: string;
  step: number;
  stepTarget: number;
  scheduledAt: string | null;
  previewAt: string | null;
  testSentAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  selfApproved: boolean;
  startedAt: string | null;
  startedBy: string | null;
  finishedAt: string | null;
  abortReason: string | null;
  autoAborted: boolean;
  createdBy: string | null;
  createdAt: string;
  preview: Preview | null;
  previewExpiresAt: string | null;
  previewJob: PreviewJob | null;
  metrics: Metrics;
  secondApproverExists?: boolean;
  ownApprovalAllowed?: boolean;
  requireAvailability: boolean;
  availabilityRaceId: string | null;
  /** Co celou rozesílku právě zadržuje (pojistka dostupnosti vstupenky). */
  hold: { reason: string; since: string | null; text: string } | null;
}

interface ListResponse {
  items: BroadcastView[];
  rules: {
    steps: number[];
    batchSize: number;
    errorThresholdPercent: number;
    stepWaitMinutes: number;
  };
}

/** Důvody u rozesílek mají vlastní, srozumitelnější znění než u zpráv cest. */
const BROADCAST_REASON: Record<string, string> = {
  template_missing: 'chybí publikovaná šablona v jazyce příjemce',
  template_not_synced: 'šablona není nahraná do SmartEmailingu',
  no_consent: 'nemá marketingový souhlas u správce značky',
  switch_off: 'vypínač odesílání rozesílek (BC) je vypnutý',
  not_in_allowlist: 'adresa není mezi testovacími adresami (prostředí Zkouška)',
  frequency_cap: 'frekvenční strop (dostal nedávno marketing)',
  support_contact: 'nedávno se ozval podpoře',
  care_hold: 'je označen péčí o zákazníky',
  send_error:
    'odeslání selhalo; zpráva se sama znovu neposílá (SmartEmailing ji mohl přijmout), vyřeší to správce níže',
};
const reasonText = (reason: string | null): string =>
  !reason ? 'bude odesláno' : (BROADCAST_REASON[reason] ?? label(REASON_LABEL, reason));

const SHOWN_STEPS = 3;

export function Broadcasts({
  rights,
  initialOpenId = null,
}: {
  rights: Rights;
  /** Otevře rovnou detail rozesílky (odkaz z plánu rozesílek). */
  initialOpenId?: number | null;
}): JSX.Element {
  const [view, setView] = useState<'broadcasts' | 'lists'>('broadcasts');
  const [openId, setOpenId] = useState<number | 'new' | null>(initialOpenId);
  const me = useMe();
  const env = me.data?.environment?.name ?? 'development';
  const query = useQuery({
    queryKey: ['broadcasts'],
    queryFn: () => api.get<ListResponse>('/api/broadcasts'),
    refetchInterval: 15_000,
    retry: false,
  });
  const canWrite = rights.write('communication');

  if (query.isLoading) return <LoadingState what="rozesílky" />;
  if (query.isError) {
    const disabled = query.error instanceof ApiError && query.error.code === 'layer_disabled';
    return disabled ? (
      <EmptyState title="Vrstva Marketingové odesílání je vypnutá" icon="layers">
        Rozesílky jsou součástí vlny 1. Zapíná ji administrátor v Nastavení → Vlny nasazení.
      </EmptyState>
    ) : (
      <ErrorState
        title="Rozesílky se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }

  if (openId !== null) {
    return (
      <BroadcastDetail
        id={openId}
        rights={rights}
        onBack={() => {
          setOpenId(null);
          void query.refetch();
        }}
        onOpen={(id) => setOpenId(id)}
      />
    );
  }

  const items = query.data?.items ?? [];
  const rules = query.data?.rules;

  return (
    <>
      <div className="seg" role="tablist" aria-label="Rozesílky">
        <button
          role="tab"
          aria-selected={view === 'broadcasts'}
          className={view === 'broadcasts' ? 'on' : ''}
          onClick={() => setView('broadcasts')}
        >
          Rozesílky
        </button>
        <button
          role="tab"
          aria-selected={view === 'lists'}
          className={view === 'lists' ? 'on' : ''}
          onClick={() => setView('lists')}
        >
          Statické seznamy
        </button>
      </div>

      {view === 'lists' ? (
        <StaticLists canWrite={canWrite} />
      ) : (
        <Panel
          title="Rozesílky"
          icon="send"
          subtitle="Jednorázové marketingové odeslání šablony na seznam. Odesílá hub: u každého příjemce znovu ověří souhlas, odhlášení a frekvenční strop."
          action={
            <div className="row">
              <span className="envtag" aria-label="Prostředí">
                <EnvBadge name={env} />
              </span>
              {canWrite ? (
                <button className="btn pri" onClick={() => setOpenId('new')}>
                  <Icon name="send" size={14} /> Nová rozesílka
                </button>
              ) : null}
            </div>
          }
        >
          {rules ? (
            <p className="note">
              Odesílá se po krocích {CANARY_STEPS_LABEL.join(' → ')} seznamu; další krok jde jen po
              potvrzení a kontrole. Při míře odskoků a chyb nad {rules.errorThresholdPercent} % se
              rozesílka zastaví sama. Dávka: {rules.batchSize} příjemců za minutu. Přerušit jde
              kdykoli.
            </p>
          ) : null}
          {items.length === 0 ? (
            <EmptyState title="Zatím žádné rozesílky" icon="send">
              {canWrite
                ? 'Založte první tlačítkem Nová rozesílka. Nejdřív si ale připravte publikovanou šablonu s kódem BC (Komunikace → Šablony).'
                : 'Rozesílky zakládá mailing specialista nebo administrátor.'}
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Rozesílka</th>
                    <th>Značka</th>
                    <th>Seznam</th>
                    <th>Stav</th>
                    <th className="n">Příjemců</th>
                    <th className="n">Odesláno</th>
                    <th className="n">Zadrženo</th>
                    <th>Čas</th>
                    <th>
                      <span className="sr-only">Akce</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <b>{b.name}</b>
                        <br />
                        <code>{b.code}</code>
                      </td>
                      <td>{b.brand}</td>
                      <td>{b.audience.label}</td>
                      <td>
                        <StatusBadge tone={BROADCAST_STATUS_TONE[b.status] ?? 'neu'}>
                          {label(BROADCAST_STATUS_LABEL, b.status)}
                        </StatusBadge>
                      </td>
                      <td className="n">{num(b.metrics.planned)}</td>
                      <td className="n">{num(b.metrics.sent)}</td>
                      <td className="n">{num(b.metrics.held + b.metrics.suppressed)}</td>
                      <td>
                        {dateTime(b.finishedAt ?? b.startedAt ?? b.scheduledAt ?? b.createdAt)}
                      </td>
                      <td>
                        <button className="btn" onClick={() => setOpenId(b.id)}>
                          Otevřít
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}

/* --------------------------------------------------------------- formulář --- */

interface FormState {
  name: string;
  brand: string;
  templateBase: string;
  fallbackLanguage: 'en' | 'none';
  kind: 'segment' | 'static_list';
  segmentCode: string;
  staticListId: string;
  windowMode: 'recipient_window' | 'asap';
  canaryEnabled: boolean;
  scheduledAt: string;
  requireAvailability: boolean;
  availabilityRaceId: string;
}

const emptyForm = (): FormState => ({
  name: '',
  brand: 'TGP',
  templateBase: '',
  fallbackLanguage: 'en',
  kind: 'segment',
  segmentCode: '',
  staticListId: '',
  windowMode: 'recipient_window',
  canaryEnabled: true,
  scheduledAt: '',
  requireAvailability: false,
  availabilityRaceId: '',
});

const fromView = (b: BroadcastView): FormState => ({
  name: b.name,
  brand: b.brand,
  templateBase: b.templateBase,
  fallbackLanguage: b.fallbackLanguage === 'none' ? 'none' : 'en',
  kind: b.audience.kind === 'static_list' ? 'static_list' : 'segment',
  segmentCode: b.audience.segmentCode ?? '',
  staticListId: b.audience.staticListId ?? '',
  windowMode: b.windowMode === 'asap' ? 'asap' : 'recipient_window',
  canaryEnabled: b.canaryEnabled,
  scheduledAt: '',
  requireAvailability: b.requireAvailability,
  availabilityRaceId: b.availabilityRaceId ?? '',
});

function BroadcastForm({
  initial,
  onSaved,
  onCancel,
  existingId,
}: {
  initial: FormState;
  existingId?: number;
  onSaved: (id: number) => void;
  onCancel: () => void;
}): JSX.Element {
  const [f, setF] = useState<FormState>(initial);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<FormState>): void => setF({ ...f, ...patch });
  const templates = useQuery({
    queryKey: ['broadcast-templates'],
    queryFn: () =>
      api.get<{ items: Array<{ base: string; brand: string; name: string; languages: string[] }> }>(
        '/api/broadcasts/templates',
      ),
  });
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () =>
      api.get<{ items: Array<{ code: string; name: string; memberCount: number }> }>(
        '/api/segments',
      ),
  });
  const lists = useQuery({
    queryKey: ['static-lists'],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; name: string; members: number }> }>('/api/static-lists'),
  });
  const raceOptions = useQuery({
    queryKey: ['broadcast-plan-races'],
    queryFn: () =>
      api.get<{
        items: Array<{ raceId: string; name: string; season: number; series: string }>;
      }>('/api/broadcast-plan/race-options'),
  });
  const families = (templates.data?.items ?? []).filter((t) => t.brand === f.brand);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        brand: f.brand,
        templateBase: f.templateBase,
        fallbackLanguage: f.fallbackLanguage,
        audience:
          f.kind === 'segment'
            ? { kind: 'segment', segmentCode: f.segmentCode }
            : { kind: 'static_list', staticListId: f.staticListId },
        windowMode: f.windowMode,
        canaryEnabled: f.canaryEnabled,
        requireAvailability: f.requireAvailability,
        availabilityRaceId: f.requireAvailability ? f.availabilityRaceId : null,
        ...(f.scheduledAt ? { scheduledAt: new Date(f.scheduledAt).toISOString() } : {}),
      };
      return existingId === undefined
        ? api.post<BroadcastView>('/api/broadcasts', body)
        : api.put<BroadcastView>(`/api/broadcasts/${existingId}`, body);
    },
    onSuccess: (b) => onSaved(b.id),
    onError: (err: Error) => setError(err.message),
  });
  const valid =
    f.name.trim() !== '' &&
    f.templateBase !== '' &&
    (f.kind === 'segment' ? f.segmentCode !== '' : f.staticListId !== '') &&
    (!f.requireAvailability || f.availabilityRaceId !== '');

  return (
    <Panel
      title={existingId === undefined ? 'Nová rozesílka' : 'Upravit koncept'}
      icon="file"
      subtitle="Po uložení rozesílku zkontrolujete (náhled příjemců), zkušebně odešlete a nechá schválit druhým člověkem."
    >
      {error ? (
        <ErrorState title="Rozesílku se nepodařilo uložit" error={new Error(error)} />
      ) : null}
      <div className="bcform">
        <Field label="Název" hint="Při schvalování a spuštění se opisuje.">
          <input value={f.name} maxLength={200} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="Značka">
          <BrandSelect
            value={f.brand}
            allLabel={null}
            shopOnly
            onChange={(code) => set({ brand: code, templateBase: '' })}
          />
        </Field>
        <Field
          label="Šablona"
          hint={
            families.length === 0
              ? 'Pro tuto značku není publikovaná žádná šablona s kódem BC (Komunikace → Šablony, kód BC Rozesílka).'
              : 'Každý příjemce dostane šablonu ve svém jazyce.'
          }
        >
          <select value={f.templateBase} onChange={(e) => set({ templateBase: e.target.value })}>
            <option value="">— vyberte —</option>
            {families.map((t) => (
              <option key={t.base} value={t.base}>
                {t.base} ({t.languages.join(', ')})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Příjemce bez šablony svého jazyka">
          <select
            value={f.fallbackLanguage}
            onChange={(e) => set({ fallbackLanguage: e.target.value === 'none' ? 'none' : 'en' })}
          >
            <option value="en">Poslat anglickou šablonu</option>
            <option value="none">Nic neposílat (zadržet s důvodem)</option>
          </select>
        </Field>
        <Field label="Zdroj seznamu">
          <select
            value={f.kind}
            onChange={(e) =>
              set({ kind: e.target.value === 'static_list' ? 'static_list' : 'segment' })
            }
          >
            <option value="segment">Uložený segment (aktuální členové)</option>
            <option value="static_list">Statický seznam adres</option>
          </select>
        </Field>
        <p className="note">
          Povinné výluky platí vždy a nejdou odebrat: odhlášení, blacklist, tvrdý odskok, bez
          souhlasu, kontrolní skupina, okno závodu, frekvenční strop a vylučovací seznamy SE
          (podrobně v Segmenty, Povinné výluky). Vyřazený příjemce má v náhledu uvedený důvod.
        </p>
        {f.kind === 'segment' ? (
          <Field label="Segment">
            <select value={f.segmentCode} onChange={(e) => set({ segmentCode: e.target.value })}>
              <option value="">— vyberte —</option>
              {(segments.data?.items ?? []).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name} ({num(s.memberCount)})
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Statický seznam">
            <select value={f.staticListId} onChange={(e) => set({ staticListId: e.target.value })}>
              <option value="">— vyberte —</option>
              {(lists.data?.items ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({num(l.members)})
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Kdy odesílat">
          <select
            value={f.windowMode}
            onChange={(e) =>
              set({ windowMode: e.target.value === 'asap' ? 'asap' : 'recipient_window' })
            }
          >
            {Object.entries(WINDOW_MODE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Navržený začátek"
          hint="Prázdné = hned po naplánování. Plánuje se až po schválení."
        >
          <input
            type="datetime-local"
            value={f.scheduledAt}
            onChange={(e) => set({ scheduledAt: e.target.value })}
          />
        </Field>
        <label className="row bcchk">
          <input
            type="checkbox"
            checked={f.canaryEnabled}
            onChange={(e) => set({ canaryEnabled: e.target.checked })}
          />
          <span>
            Uvolňovat postupně ({CANARY_STEPS_LABEL.join(' → ')}), další krok jen po potvrzení
          </span>
        </label>
        <label className="row bcchk">
          <input
            type="checkbox"
            checked={f.requireAvailability}
            onChange={(e) => set({ requireAvailability: e.target.checked })}
          />
          <span>
            Odeslat jen když je na závod dostupná vstupenka (jinak se rozesílka zadrží a hub
            upozorní, nic se nezruší)
          </span>
        </label>
        {f.requireAvailability ? (
          <Field
            label="Závod pro kontrolu dostupnosti"
            hint="Kontroluje se při naplánování a před každou dávkou."
          >
            <select
              value={f.availabilityRaceId}
              onChange={(e) => set({ availabilityRaceId: e.target.value })}
            >
              <option value="">Vyberte závod</option>
              {(raceOptions.data?.items ?? []).map((r) => (
                <option key={r.raceId} value={r.raceId}>
                  {raceOptionLabel(r)}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>
      <div className="row">
        <button
          className="btn pri"
          disabled={!valid || save.isPending}
          onClick={() => save.mutate()}
        >
          Uložit koncept
        </button>
        <button className="btn" onClick={onCancel}>
          Zrušit
        </button>
      </div>
    </Panel>
  );
}

/* ----------------------------------------------------------------- detail --- */

type Step = { key: string; title: string; state: 'done' | 'now' | 'todo'; text: string };

function stepsOf(b: BroadcastView): Step[] {
  const order = ['draft', 'preview', 'tested', 'approved', 'scheduled', 'sending'];
  const at = order.indexOf(b.status === 'awaiting_step' ? 'sending' : b.status);
  const done = (i: number): boolean => b.status === 'completed' || at > i;
  const stateOf = (i: number): Step['state'] =>
    done(i) ? 'done' : at === i || (at === i - 1 && b.status !== 'aborted') ? 'now' : 'todo';
  return [
    {
      key: 'create',
      title: '1. Šablona, seznam a čas',
      state: 'done',
      text: 'Uloženo v konceptu.',
    },
    {
      key: 'preview',
      title: '2. Kontrola: kdo dostane a kdo ne',
      state: done(0) ? 'done' : stateOf(0),
      text:
        b.previewJob?.state === 'running'
          ? 'Počítá se na pozadí.'
          : b.previewAt
            ? `Spočítáno ${dateTime(b.previewAt)}${b.previewExpiresAt ? `, platí do ${dateTime(b.previewExpiresAt)}` : ''}.`
            : 'Spočítejte příjemce.',
    },
    {
      key: 'test',
      title: '3. Zkušební odeslání',
      state: done(1) ? 'done' : stateOf(1),
      text: b.testSentAt ? `Odesláno ${dateTime(b.testSentAt)}.` : 'Jen na testovací adresy.',
    },
    {
      key: 'approve',
      title: '4. Schválení druhým člověkem',
      state: done(2) ? 'done' : stateOf(2),
      text: b.approvedAt
        ? `Schválil ${b.approvedBy ?? '—'} (${dateTime(b.approvedAt)})${b.selfApproved ? ', vlastní rozesílku s potvrzením administrátora' : ''}.`
        : 'Schvaluje jiný oprávněný uživatel než tvůrce.',
    },
    {
      key: 'schedule',
      title: '5. Naplánování odeslání',
      state: done(3) ? 'done' : stateOf(3),
      text: b.scheduledAt ? `Začátek ${dateTime(b.scheduledAt)}.` : 'Určete začátek a potvrďte.',
    },
    {
      key: 'send',
      title: '6. Odesílání po krocích',
      state:
        b.status === 'completed'
          ? 'done'
          : b.status === 'sending' || b.status === 'awaiting_step'
            ? 'now'
            : 'todo',
      text: b.canaryEnabled
        ? `Krok ${Math.min(b.step + 1, SHOWN_STEPS)} ze ${SHOWN_STEPS} (${CANARY_STEPS_LABEL[Math.min(b.step, 2)]}).`
        : 'Všem najednou (po dávkách).',
    },
  ];
}

function BroadcastDetail({
  id,
  rights,
  onBack,
  onOpen,
}: {
  id: number | 'new';
  rights: Rights;
  onBack: () => void;
  onOpen: (id: number) => void;
}): JSX.Element {
  const client = useQueryClient();
  const me = useMe();
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [dialog, setDialog] = useState<'approve' | 'schedule' | 'step' | 'abort' | null>(null);
  const [abortReason, setAbortReason] = useState('');
  const [selfConfirmed, setSelfConfirmed] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [recipient, setRecipient] = useState('');
  const env = me.data?.environment?.name ?? 'development';
  const production = env === 'production';

  const query = useQuery({
    queryKey: ['broadcast', id],
    enabled: id !== 'new',
    queryFn: () => api.get<BroadcastView>(`/api/broadcasts/${id}`),
    // Dokud se počítá náhled na pozadí, stav se zjišťuje častěji.
    refetchInterval: (q) => (q.state.data?.previewJob?.state === 'running' ? 2_000 : 10_000),
  });
  const tests = useQuery({
    queryKey: ['send-tests'],
    enabled: id !== 'new' && !production,
    queryFn: () =>
      api.get<{ recipients: Array<{ address: string; masked: string }> }>('/api/send-tests'),
    retry: false,
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['broadcast', id] });
    void client.invalidateQueries({ queryKey: ['broadcasts'] });
    void client.invalidateQueries({ queryKey: ['broadcast-recipients', id] });
  };
  const fail = (err: Error): void => {
    setNote({ tone: 'bad', text: err.message });
    setDialog(null);
  };
  const action = <T,>(path: string, body: unknown, ok: (r: T) => string) =>
    useMutation({
      mutationFn: () => api.post<T>(`/api/broadcasts/${id}/${path}`, body),
      onSuccess: (r) => {
        setNote({ tone: 'ok', text: ok(r) });
        setDialog(null);
        refresh();
      },
      onError: fail,
    });

  const b = query.data;
  const preview = action<PreviewResponse>('preview', {}, (r) =>
    'computing' in r
      ? 'Seznam je velký, náhled se počítá na pozadí. Stránka sama ukáže průběh a výsledek.'
      : `Náhled hotový: dostane ${num(r.willSend)} z ${num(r.total)}.`,
  );
  const cancelPreview = action<unknown>(
    'preview/cancel',
    {},
    () => 'Výpočet náhledu byl zrušen. Nic se neuložilo.',
  );
  const chosen =
    recipient ||
    (tests.data?.recipients.length === 1 ? (tests.data.recipients[0]?.address ?? '') : '');
  const test = action<{ status: string; error: string | null; recipientMasked: string }>(
    'test-send',
    chosen ? { recipient: chosen } : {},
    (r) =>
      r.status === 'failed'
        ? `Zkušební odeslání selhalo: ${r.error ?? ''}`
        : `Zkušební zpráva (${r.status === 'dry_run' ? 'nanečisto' : 'odeslána'}) na ${r.recipientMasked}.`,
  );
  const approve = action<BroadcastView>(
    'approve',
    { confirmName: b?.name ?? '', ...(selfConfirmed ? { selfApprovalConfirmed: true } : {}) },
    () => 'Rozesílka je schválená. Teď ji naplánujte.',
  );
  const schedule = action<BroadcastView>(
    'schedule',
    {
      confirmName: b?.name ?? '',
      ...(startAt ? { scheduledAt: new Date(startAt).toISOString() } : {}),
    },
    () => 'Rozesílka je naplánovaná.',
  );
  const confirmStep = action<BroadcastView>(
    'confirm-step',
    { confirmName: b?.name ?? '' },
    () => 'Další krok je uvolněn.',
  );
  const abort = action<BroadcastView>(
    'abort',
    { reason: abortReason || 'ruční přerušení' },
    () => 'Rozesílka je přerušena.',
  );

  if (id === 'new' || editing) {
    return (
      <BroadcastForm
        initial={b && editing ? fromView(b) : emptyForm()}
        {...(editing && b ? { existingId: b.id } : {})}
        onSaved={(newId) => {
          setEditing(false);
          refresh();
          onOpen(newId);
        }}
        onCancel={() => (editing ? setEditing(false) : onBack())}
      />
    );
  }
  if (query.isLoading) return <LoadingState what="rozesílku" />;
  if (query.isError || !b)
    return (
      <ErrorState
        title="Rozesílku se nepodařilo načíst"
        error={query.error ?? new Error('Rozesílka neexistuje.')}
        onRetry={() => void query.refetch()}
      />
    );

  const canSend = rights.can('send_broadcast');
  const canWrite = rights.write('communication');
  const editable = ['draft', 'preview', 'tested'].includes(b.status);
  const computing = b.previewJob?.state === 'running';
  const live = ['approved', 'scheduled', 'sending', 'awaiting_step'].includes(b.status);
  const m = b.metrics;
  const sameUser = b.createdBy !== null && me.data?.user.name === b.createdBy;
  const mustHaveOther = sameUser && b.secondApproverExists === true && !b.ownApprovalAllowed;
  const needsTest = !production;
  const approvable =
    (b.status === 'tested' || (!needsTest && b.status === 'preview')) && canSend && !computing;

  return (
    <>
      <PageHeader
        level={2}
        title={b.name}
        description={
          <>
            <code>{b.code}</code> · značka {b.brand} · seznam „{b.audience.label}“ ·{' '}
            {label(WINDOW_MODE_LABEL, b.windowMode)}
            {b.requireAvailability
              ? ` · odešle se, jen když je dostupná vstupenka (${b.availabilityRaceId ?? ''})`
              : ''}
          </>
        }
        actions={
          <div className="row">
            <span className="envtag" aria-label="Prostředí">
              <EnvBadge name={env} />
            </span>
            <StatusBadge tone={BROADCAST_STATUS_TONE[b.status] ?? 'neu'}>
              {label(BROADCAST_STATUS_LABEL, b.status)}
            </StatusBadge>
            <button className="btn" onClick={onBack}>
              <Icon name="back" size={14} /> Všechny rozesílky
            </button>
          </div>
        }
      />

      {note ? (
        <p className={`note${note.tone === 'bad' ? ' bad' : ''}`} role="status">
          {note.text}
        </p>
      ) : null}

      {b.status === 'aborted' ? (
        <p className="note bad" role="alert">
          <b>Rozesílka je přerušena{b.autoAborted ? ' automaticky' : ''}.</b> {b.abortReason}. Už
          odeslané zůstává v historii, zbytek příjemců zprávu nedostal. Přerušenou rozesílku nejde
          obnovit; po odstranění příčiny založte novou.
        </p>
      ) : null}
      {b.hold ? (
        <p className="note bad" role="alert">
          <Icon name="alert" size={14} /> <b>Rozesílka je zadržena.</b> {b.hold.text} Příjemci
          nejsou zrušeni; po obnovení prodeje se odesílání samo rozběhne. Nechcete-li čekat,
          rozesílku přerušte.
        </p>
      ) : null}
      {b.status === 'awaiting_step' ? (
        <p className="note" role="status">
          <b>Krok {b.step + 1} je hotový.</b> Zkontrolujte odskoky a chyby níže a potvrďte další
          krok. Odeslat jde nejdřív po uplynutí čekací doby na reakce.
        </p>
      ) : null}

      <Panel title="Postup" icon="route">
        <ol className="bcsteps">
          {stepsOf(b).map((s) => (
            <li key={s.key} className={s.state}>
              <span className="bcmark" aria-hidden="true">
                {s.state === 'done' ? <Icon name="check" size={14} /> : null}
              </span>
              <span>
                <b>{s.title}</b>
                <small>
                  {s.text}
                  <span className="sr-only">
                    {s.state === 'done'
                      ? ' (hotovo)'
                      : s.state === 'now'
                        ? ' (na řadě)'
                        : ' (později)'}
                  </span>
                </small>
              </span>
            </li>
          ))}
        </ol>

        <div className="row bcactions">
          {canWrite && editable ? (
            <>
              <button className="btn" disabled={computing} onClick={() => setEditing(true)}>
                Upravit koncept
              </button>
              <button
                className="btn pri"
                disabled={preview.isPending || computing}
                onClick={() => preview.mutate()}
              >
                {b.preview ? 'Přepočítat příjemce' : 'Zkontrolovat příjemce'}
              </button>
            </>
          ) : null}
          {canWrite &&
          (b.status === 'preview' || b.status === 'tested') &&
          !production &&
          !computing ? (
            <>
              {tests.data && tests.data.recipients.length > 1 ? (
                <select
                  aria-label="Zkušební příjemce"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                >
                  <option value="">— zkušební adresa —</option>
                  {tests.data.recipients.map((r) => (
                    <option key={r.address} value={r.address}>
                      {r.masked}
                    </option>
                  ))}
                </select>
              ) : null}
              <button
                className="btn"
                disabled={
                  test.isPending ||
                  (tests.data ? tests.data.recipients.length > 1 && !chosen : false)
                }
                onClick={() => test.mutate()}
              >
                Zkušební odeslání
              </button>
            </>
          ) : null}
          {production && (b.status === 'preview' || b.status === 'tested') ? (
            <span className="hint">
              V Produkci se zkušebně neodesílá; rozesílku odzkoušejte ve Zkoušce.
            </span>
          ) : null}
          {approvable ? (
            <button
              className="btn pri"
              disabled={mustHaveOther}
              title={mustHaveOther ? 'Rozesílku nesmí schválit ten, kdo ji vytvořil.' : undefined}
              onClick={() => setDialog('approve')}
            >
              Schválit
            </button>
          ) : null}
          {b.status === 'approved' && canSend ? (
            <button className="btn pri" onClick={() => setDialog('schedule')}>
              Naplánovat odeslání
            </button>
          ) : null}
          {b.status === 'awaiting_step' && canSend ? (
            <button className="btn pri" onClick={() => setDialog('step')}>
              Potvrdit další krok
            </button>
          ) : null}
          {live && canSend ? (
            <button className="btn dng" onClick={() => setDialog('abort')}>
              Přerušit
            </button>
          ) : null}
        </div>
        {mustHaveOther && approvable ? (
          <p className="hint">
            Tvůrce nesmí schválit vlastní rozesílku, protože existuje další oprávněný uživatel.
            Požádejte ho o schválení.
          </p>
        ) : null}
        {!canSend && editable ? (
          <p className="hint">
            Schvalovat a spouštět rozesílky smí mailing specialista a administrátor.
          </p>
        ) : null}
      </Panel>

      {b.previewJob && b.previewJob.state !== 'done' ? (
        <PreviewJobPanel
          job={b.previewJob}
          canCancel={canWrite}
          cancelling={cancelPreview.isPending}
          onCancel={() => cancelPreview.mutate()}
        />
      ) : null}

      {b.preview ? <PreviewPanel preview={b.preview} expiresAt={b.previewExpiresAt} /> : null}

      {m.planned > 0 && !editable ? (
        <Panel title="Průběh" icon="chart">
          <div className="tiles">
            <Tile label="Adres celkem" value={num(m.planned)} />
            <Tile label="Odesláno" value={num(m.sent)} note={`z toho čeká ${num(m.pending)}`} />
            <Tile label="Doručeno" value={num(m.delivered)} />
            <Tile label="Otevřeno" value={num(m.opened)} />
            <Tile label="Kliknuto" value={num(m.clicked)} />
            <Tile label="Odskoky" value={num(m.bounced)} bad={m.bounced > 0} />
            <Tile label="Chyby odeslání" value={num(m.failed)} bad={m.failed > 0} />
            <Tile label="Odhlášeno" value={num(m.unsubscribed)} />
            <Tile
              label="Zadrženo"
              value={num(m.held + m.suppressed + m.expired)}
              note="důvody níže"
            />
          </div>
          <p className="note">
            Míra odskoků a chyb: {Math.round(m.badRate * 1000) / 10} % z {num(m.attempts)}{' '}
            odeslaných. Postup: {num(m.sent + m.failed)} z{' '}
            {num(Math.max(1, m.planned - m.suppressed))} adres, které smějí dostat zprávu.
          </p>
        </Panel>
      ) : null}

      {m.failed > 0 && !computing ? (
        <SendErrorsPanel broadcast={b} canSend={canSend} onChanged={refresh} />
      ) : null}

      {m.planned > 0 && !computing ? <Recipients id={b.id} /> : null}

      <ConfirmDialog
        open={dialog === 'approve'}
        title="Schválit rozesílku?"
        confirmLabel="Schválit"
        requireWord={b.name}
        busy={approve.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => approve.mutate()}
      >
        <p>
          Schvalujete rozesílku <b>{b.name}</b> pro {num(b.preview?.willSend)} příjemců. Po
          schválení ji nejde upravit. Samotné odeslání se spouští až dalším krokem.
        </p>
        {sameUser && b.ownApprovalAllowed ? (
          <p className="hint" role="alert">
            Schvalujete vlastní rozesílku – povoleno jen ve Zkoušce (dočasné uvolnění, zapíše se do
            auditu).
          </p>
        ) : null}
        {sameUser && (!b.secondApproverExists || b.ownApprovalAllowed) ? (
          <label className="row">
            <input
              type="checkbox"
              checked={selfConfirmed}
              onChange={(e) => setSelfConfirmed(e.target.checked)}
            />
            <span>
              Jste tvůrcem a jediným oprávněným: schvaluji vlastní rozesílku (zapíše se do auditu).
            </span>
          </label>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'schedule'}
        title="Naplánovat a spustit odeslání?"
        confirmLabel="Naplánovat odeslání"
        danger
        requireWord={b.name}
        busy={schedule.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => schedule.mutate()}
      >
        <p>
          Zprávy začnou odcházet zákazníkům v prostředí{' '}
          <b>{label({ development: 'Vývoj', staging: 'Zkouška', production: 'Produkce' }, env)}</b>{' '}
          v režimu{' '}
          <b>
            {b.preview?.mode === 'live'
              ? 'ostrý'
              : b.preview?.mode === 'test_allowlist'
                ? 'testovací adresy'
                : 'nanečisto'}
          </b>
          . Nejdřív odejde první krok ({CANARY_STEPS_LABEL[0]}), další jen po vašem potvrzení.
        </p>
        <Field label="Začátek (prázdné = hned)">
          <input
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'step'}
        title="Potvrdit další krok?"
        confirmLabel="Uvolnit další krok"
        requireWord={b.name}
        busy={confirmStep.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => confirmStep.mutate()}
      >
        Odskoky: {num(m.bounced)}, chyby: {num(m.failed)} z {num(m.attempts)} odeslaných (
        {Math.round(m.badRate * 1000) / 10} %). Hub míru ještě jednou ověří; je-li nad prahem,
        rozesílka se zastaví.
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'abort'}
        title="Přerušit rozesílku?"
        confirmLabel="Přerušit rozesílku"
        danger
        busy={abort.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => abort.mutate()}
      >
        <p>Další zprávy přestanou odcházet hned. Odeslané se nevrací a rozesílku nejde obnovit.</p>
        <Field label="Důvod přerušení">
          <input
            value={abortReason}
            maxLength={500}
            onChange={(e) => setAbortReason(e.target.value)}
          />
        </Field>
      </ConfirmDialog>
    </>
  );
}

/* ------------------------------------------------------------- náhled příjemců --- */

function PreviewJobPanel({
  job,
  canCancel,
  cancelling,
  onCancel,
}: {
  job: PreviewJob;
  canCancel: boolean;
  cancelling: boolean;
  onCancel: () => void;
}): JSX.Element {
  if (job.state === 'running')
    return (
      <Panel
        title="Náhled se počítá"
        icon="clock"
        subtitle="Seznam je velký, počítá se na pozadí. Nic se neodesílá; stránku můžete nechat otevřenou nebo se k ní vrátit později."
      >
        <div className="bcprogress">
          <p role="status" aria-live="polite">
            {job.claimed
              ? job.total === null
                ? 'Načítám seznam…'
                : `Zpracováno ${num(job.processed)} z ${num(job.total)} adres.`
              : 'Čeká na spuštění (úloha běží každou minutu).'}
          </p>
          <Bar value={job.processed} max={job.total ?? 0} />
        </div>
        {canCancel ? (
          <div className="row bcactions">
            <button className="btn" disabled={cancelling} onClick={onCancel}>
              Zrušit výpočet
            </button>
          </div>
        ) : null}
      </Panel>
    );
  if (job.state === 'failed')
    return (
      <p className="note bad" role="alert">
        <b>Výpočet náhledu selhal.</b> {job.error ?? ''} Spusťte „Zkontrolovat příjemce“ znovu.
      </p>
    );
  return (
    <p className="note" role="status">
      Výpočet náhledu byl zrušen, nic se neuložilo. Spusťte „Zkontrolovat příjemce“ znovu.
    </p>
  );
}

function PreviewPanel({
  preview,
  expiresAt,
}: {
  preview: Preview;
  expiresAt: string | null;
}): JSX.Element {
  const groups = preview.reasons;
  return (
    <Panel
      title="Kdo dostane a kdo ne"
      icon="users"
      subtitle={`Z ${num(preview.total)} adres dostane zprávu ${num(preview.willSend)}. Ostatní mají důvod; nic z toho se neodesílá.${expiresAt ? ` Náhled platí do ${dateTime(expiresAt)}.` : ''}`}
    >
      {preview.globalHold ? (
        <p className="note bad" role="alert">
          Odeslání teď zadržuje: {reasonText(preview.globalHold)}. Vyřešte to a přepočítejte
          příjemce.
        </p>
      ) : null}
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Výsledek</th>
              <th>Důvod</th>
              <th className="n">Adres</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={`${g.status}:${g.reason}`}>
                <td>
                  <StatusBadge
                    tone={g.status === 'pending' ? 'ok' : g.status === 'held' ? 'warn' : 'neu'}
                  >
                    {label(RECIPIENT_STATUS_LABEL, g.status)}
                  </StatusBadge>
                </td>
                <td>{reasonText(g.reason || null)}</td>
                <td className="n">{num(g.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.languages.length > 0 ? (
        <p className="note">
          Jazyky příjemců:{' '}
          {preview.languages.map((l) => `${l.language} ${num(l.count)}`).join(', ')}.
        </p>
      ) : null}
      {preview.sample.length > 0 ? (
        <details>
          <summary>Ukázka adres (maskované)</summary>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Adresa</th>
                  <th>Jazyk</th>
                  <th>Výsledek</th>
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((s, i) => (
                  <tr key={i}>
                    <td>{s.email}</td>
                    <td>{s.language ?? '—'}</td>
                    <td>{reasonText(s.reason)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </Panel>
  );
}

/* ------------------------------------------------ příjemci s chybou odeslání --- */

interface SendErrorList {
  total: number;
  canResend: boolean;
  tag: string;
  items: Array<{
    recipientId: number;
    email: string;
    messageId: string | null;
    failedAt: string;
    error: string | null;
    reaction: { found: boolean; events: Array<{ type: string; at: string; count: number }> };
  }>;
}

type SendErrorItem = SendErrorList['items'][number];

/**
 * Příjemci, u kterých odeslání skončilo chybou (`send_error`). Hub je nikdy neposílá znovu sám:
 * SmartEmailing zprávu mohl přijmout. Správce po ověření ve SmartEmailingu zprávu označí jako odeslanou,
 * nebo ji (po potvrzení a s důvodem) odešle znovu.
 */
function SendErrorsPanel({
  broadcast: b,
  canSend,
  onChanged,
}: {
  broadcast: BroadcastView;
  canSend: boolean;
  onChanged: () => void;
}): JSX.Element {
  const client = useQueryClient();
  const [dialog, setDialog] = useState<{ kind: 'sent' | 'resend'; item: SendErrorItem } | null>(
    null,
  );
  const [reason, setReason] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const query = useQuery({
    queryKey: ['broadcast-send-errors', b.id],
    queryFn: () => api.get<SendErrorList>(`/api/broadcasts/${b.id}/send-errors?limit=50`),
    refetchInterval: 15_000,
  });
  const done = (text: string): void => {
    setNote({ tone: 'ok', text });
    setDialog(null);
    setReason('');
    void client.invalidateQueries({ queryKey: ['broadcast-send-errors', b.id] });
    onChanged();
  };
  const failed = (err: Error): void => {
    setNote({ tone: 'bad', text: err.message });
    setDialog(null);
    void client.invalidateQueries({ queryKey: ['broadcast-send-errors', b.id] });
    onChanged();
  };
  const markSent = useMutation({
    mutationFn: (item: SendErrorItem) =>
      api.post(`/api/broadcasts/${b.id}/send-errors/${item.recipientId}/mark-sent`, {}),
    onSuccess: () => done('Příjemce je označen jako odeslaný. Nic se neodeslalo.'),
    onError: failed,
  });
  const resend = useMutation({
    mutationFn: (item: SendErrorItem) =>
      api.post(`/api/broadcasts/${b.id}/send-errors/${item.recipientId}/resend`, {
        reason: reason.trim(),
        confirmWord: 'ODESLAT',
      }),
    onSuccess: () => done('Zpráva byla odeslána znovu. Záznam je v auditu.'),
    onError: failed,
  });
  const list = query.data;
  return (
    <Panel
      title="Příjemci s chybou odeslání"
      icon="alert"
      subtitle="Hub tyto zprávy nikdy neposílá znovu sám: SmartEmailing je mohl přijmout a příjemce by dostal e-mail dvakrát. Ověřte ve SmartEmailingu, zda zpráva odešla, a pak rozhodněte."
    >
      {note ? (
        <p className={`note${note.tone === 'bad' ? ' bad' : ''}`} role="status">
          {note.text}
        </p>
      ) : null}
      {query.isLoading ? <LoadingState what="příjemce s chybou" /> : null}
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}
      {list && list.items.length === 0 ? (
        <EmptyState title="Žádná chyba k vyřešení" icon="check">
          Všechny chyby odeslání jsou vyřešené.
        </EmptyState>
      ) : null}
      {list && list.items.length > 0 ? (
        <>
          <p className="hint">
            Kampaň u SmartEmailingu: <code>{list.tag}</code>. Podle ní najdete zprávu v historii
            odeslaných.
          </p>
          <div className="scroll">
            <table className="bcerrs">
              <thead>
                <tr>
                  <th>Adresa</th>
                  <th>Chyba</th>
                  <th>Událost ze SmartEmailingu</th>
                  <th>Řešení</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((it) => (
                  <tr key={it.recipientId}>
                    <td data-label="Adresa">
                      {it.email}
                      <small className="bcmuted">{dateTime(it.failedAt)}</small>
                    </td>
                    <td data-label="Chyba">{it.error ?? '—'}</td>
                    <td data-label="Událost ze SmartEmailingu">
                      {it.reaction.found ? (
                        <>
                          <StatusBadge tone="ok">Zpráva zřejmě odešla</StatusBadge>
                          <small className="bcmuted">
                            {it.reaction.events
                              .map((e) => `${label(REACTION_LABEL, e.type)} ${dateTime(e.at)}`)
                              .join(', ')}
                          </small>
                        </>
                      ) : (
                        <StatusBadge tone="neu">Žádná událost (zatím)</StatusBadge>
                      )}
                    </td>
                    <td data-label="Řešení">
                      {canSend ? (
                        <div className="bcerrbtns">
                          <button
                            className="btn"
                            onClick={() => setDialog({ kind: 'sent', item: it })}
                          >
                            Ověřeno: odešlo
                          </button>
                          {list.canResend ? (
                            <button
                              className="btn dng"
                              onClick={() => {
                                setReason('');
                                setDialog({ kind: 'resend', item: it });
                              }}
                            >
                              Ověřeno: neodešlo, odeslat znovu
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <span className="hint">Řeší oprávněný správce rozesílek.</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {list.total > list.items.length ? (
            <p className="hint">
              Zobrazeno {num(list.items.length)} z {num(list.total)}.
            </p>
          ) : null}
          {!list.canResend ? (
            <p className="hint">
              Rozesílka už neběží, proto jde zprávu jen označit jako odeslanou. Znovu se u ní
              neodesílá.
            </p>
          ) : null}
        </>
      ) : null}

      <ConfirmDialog
        open={dialog?.kind === 'sent'}
        title="Označit jako odeslané?"
        confirmLabel="Ano, zpráva odešla"
        busy={markSent.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => dialog && markSent.mutate(dialog.item)}
      >
        <p>
          Potvrzujete, že jste ve SmartEmailingu ověřili, že zpráva pro <b>{dialog?.item.email}</b>{' '}
          odešla. Příjemce se označí jako odeslaný a nic se neodešle. Záznam je v auditu.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog?.kind === 'resend'}
        title="Odeslat zprávu znovu?"
        confirmLabel="Odeslat znovu"
        danger
        requireWord="ODESLAT"
        confirmDisabled={reason.trim().length < 3}
        busy={resend.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => dialog && resend.mutate(dialog.item)}
      >
        <p className="note bad" role="alert">
          <b>Pozor, hrozí dvojí e-mail.</b> SmartEmailing mohl první zprávu přijmout, i když hub
          dostal chybu. Pokračujte, jen když jste ve SmartEmailingu ověřili, že zpráva pro{' '}
          <b>{dialog?.item.email}</b> <b>neodešla</b>. Příjemce znovu projde všemi kontrolami
          (souhlas, odhlášení, přepínače); zpráva se odešle nejvýš jednou.
        </p>
        <Field label="Důvod (povinný, zapíše se do auditu)">
          <input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </ConfirmDialog>
    </Panel>
  );
}

function Recipients({ id }: { id: number }): JSX.Element {
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ['broadcast-recipients', id, status, offset],
    queryFn: () =>
      api.get<{
        total: number;
        items: Array<{ email: string; status: string; reason: string | null }>;
      }>(
        `/api/broadcasts/${id}/recipients?limit=25&offset=${offset}${status ? `&status=${status}` : ''}`,
      ),
  });
  return (
    <Panel
      title="Příjemci"
      icon="users"
      action={
        <select
          aria-label="Filtr výsledku"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">Všichni</option>
          {Object.entries(RECIPIENT_STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      }
    >
      {query.isLoading ? <LoadingState what="příjemce" /> : null}
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}
      {query.data ? (
        query.data.items.length === 0 ? (
          <EmptyState title="Nikdo takový" icon="users">
            Pro tento filtr nejsou žádní příjemci.
          </EmptyState>
        ) : (
          <>
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Adresa</th>
                    <th>Výsledek</th>
                    <th>Důvod</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((r, i) => (
                    <tr key={`${r.email}-${i}`}>
                      <td>{r.email}</td>
                      <td>{label(RECIPIENT_STATUS_LABEL, r.status)}</td>
                      <td>{reasonText(r.reason)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row">
              <button
                className="btn"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - 25))}
              >
                Předchozí
              </button>
              <span className="hint">
                {num(offset + 1)}–{num(Math.min(offset + 25, query.data.total))} z{' '}
                {num(query.data.total)}
              </span>
              <button
                className="btn"
                disabled={offset + 25 >= query.data.total}
                onClick={() => setOffset(offset + 25)}
              >
                Další
              </button>
            </div>
          </>
        )
      ) : null}
    </Panel>
  );
}
